import { apiClient } from '@/services/api'

// Backend emits structured error codes for known-recoverable conditions
// (PlaylistController + PlayoutController + StreamDeckController). These are a
// FALLBACK for when the server sends a code but no usable message — see
// describeError, which prefers what the server actually said.
//
// They used to take priority, and that made the panel lie. One code covers several
// causes: CENA_NOT_PUBLISHED is raised both for "not published yet" (where activating
// IS the fix) and for "this cena cannot be published at all" — a template missing from
// the site's PDS, say, which fails at activation and on every take alike. The server
// knows which; the client does not. Showing this text over the server's meant an
// operator was told to activate, activated, saw the same error, and had no way to
// learn the real reason. CENA_ON_AIR and ENGINE_BUSY are handled by callers
// (modals/toasts) and not mapped here.
const ERROR_CODE_MESSAGES = {
    CENA_NOT_PUBLISHED: 'Cena não está publicada no MSE. Ative a playlist para publicá-la.',
    NO_PUBLISHED_ITEM: 'Nenhuma cena publicada na playlist. Ative a playlist para publicar as cenas.',
    NO_ON_AIR_CENA: 'Nenhuma cena no ar.',
    NO_ACTIVE_PLAYLIST: 'Nenhuma playlist ativa para esta produção.',
    CENA_WRONG_PRODUCAO: 'Esta cena pertence a outra produção.',
    MSE_ELEMENT_PATH_UNRESOLVED: 'Caminho do elemento no MSE não pôde ser resolvido. A cena pode precisar ser republicada.',
}

function describeError(error, fallback) {
    // Server message first: it is the only party that knows WHY, and for the playout
    // codes it already answers in operator-facing Portuguese. The code map is what we
    // fall back on when the server sent a code but nothing readable with it.
    const data = error?.response?.data
    if (data?.error) return data.error
    if (data?.code && ERROR_CODE_MESSAGES[data.code]) return ERROR_CODE_MESSAGES[data.code]
    return fallback
}

// Every playout command that moves the on-air marker bumps this. The background
// reconcile that follows a command carries the value it was issued under and drops
// its result if another command has landed since — otherwise an in-flight state
// read from the previous take resolves after the next one and walks the on-air
// badge backwards on screen.
let onAirEpoch = 0

// The production the epoch belongs to. An in-flight read from production P must not
// commit into production Q after the operator switches: item ids are global, so P's
// answer looks structurally valid in Q and would show Q's operator P's on-air graphic,
// P's engine and P's preview URL.
let onAirProducao = null

// Invalidates every read already in flight: a command has landed that moves the on-air
// marker, so any answer still on the wire describes the world before it. Returns the new
// epoch, so the caller can tell whether its OWN follow-up reads are still wanted.
//
// reconcilePlayoutState is the obvious caller but never was the only one. Activate and
// Deactivate move the marker just as much — deactivate clears it outright — and they
// left the epoch alone, so a reconcile issued by the Take just before them stayed valid,
// resolved afterwards, and put the ON AIR badge and the red pipe back on a playlist that
// was already off air.
//
// It deliberately does NOT touch `onAirProducao`. That is reconcile's to set, because
// reconcile has a read in flight that needs scoping; a command has nothing to re-scope.
// Writing it here was actively harmful: these callers run AFTER an awaited request, so if
// the operator changed production while the command was in flight, the write landed after
// clearPlayoutForProductionSwitch had reset it — re-pinning the module to the production
// they just LEFT. Nothing resets it except another switch, so every later poll for the
// production they are now looking at failed the production check and was dropped. The
// panel froze on the old rundown, badge included, and a Next pressed there drives the old
// playlist's id: it would take the wrong production's graphic to air.
function invalidateOnAirReads() {
    return ++onAirEpoch
}

// Whether a read that was issued at `epoch` may still commit.
//
// The rule is "drop", not "correct". An earlier version re-applied the last locally
// confirmed marker over any read that crossed a command — which pins the badge to OUR
// last take even when the read observed a LATER take by another operator, and does so
// coherently (row and header agreeing, both wrong). Dropping keeps whatever we already
// display, which already has the confirmed marker on it, and lets the reconcile issued
// after the command supply the truth.
function readIsStale(epoch, codProducao) {
    if (epoch !== onAirEpoch) return true
    if (codProducao != null && onAirProducao != null && String(codProducao) !== String(onAirProducao)) return true
    return false
}

const state = () => ({
    playlists: [],
    activePlaylist: null,
    playoutState: null,
    loading: false,
    lastError: null,
    // The last activation's publish outcome: { errors: [{itemId, nome, message}], publishing }.
    // Separate from `lastError` because an activation that refuses some graphics still
    // SUCCEEDS — the playlist goes live and the rest of the rundown is takeable — so it
    // must not read as a failed command. Null when no activation has reported yet.
    //
    // Kept in the store rather than the panel because it shares `lastError`'s lifecycle:
    // one command produces it, the next command or a production switch invalidates it.
    activationReport: null,
    // FE-local: the playlist item the operator selected to PREVIEW (big render in
    // the "Cena em Exibição" block). Purely a UI selection — NOT on-air, no Take,
    // no engine. Holds the item id so the getter always resolves the FRESH item
    // from the polled playlist (a newsroom edit updates the preview automatically).
    previewSelectionId: null,
    // FE-local: a saved cena (from the Templates e cenas block) selected to PREVIEW +
    // EDIT — the same Preview/Editor surfaces the playlist items use. Mutually
    // exclusive with previewSelectionId. Identifies WHICH cena is selected;
    // getterPreviewItem resolves the cena's current data from globographics/savedScenes
    // and normalizes it into a playlist-item-like shape so both blocks consume it as-is.
    previewCena: null,
    // Result of a native engine-rendered preview cue for the selected item:
    // { idItem, signalingUrl, engineKey } once the engine has it cued, else null.
    // The preview block subscribes to signalingUrl over WebRTC (Route A).
    previewCue: null,
    previewCueLoading: false,
    previewCueError: null,
})

const getters = {
    getterPlaylists: state => state.playlists,
    getterActivePlaylist: state => state.activePlaylist,
    getterPlayoutState: state => state.playoutState,
    getterPlayoutLoading: state => state.loading,
    getterPlayoutError: state => state.lastError,
    getterActivationReport: state => state.activationReport,
    getterOnAir: state => state.playoutState?.onAir || null,
    getterPreviewSelectionId: state => state.previewSelectionId,
    getterPreviewCena: state => state.previewCena,
    getterPreviewCue: state => state.previewCue,
    getterPreviewCueLoading: state => state.previewCueLoading,
    // The current preview/edit selection, normalized to a playlist-item-like shape so
    // the Preview + Editor blocks consume both sources uniformly:
    //  - a saved cena (Templates e cenas block) → resolved FRESH from savedScenes and
    //    wrapped { id_gh_cena, cena, … }; id_playlist_item is null so the blocks show
    //    its static render + edit the cena.
    //  - a playlist item → resolved FRESH from the polled playlists (a newsroom edit
    //    flows straight into the preview).
    getterPreviewItem: (state, getters, rootState) => {
        if (state.previewCena) {
            // previewCena is only the selection ANCHOR — the cena's data is read from
            // savedScenes so an autosave flows straight through. Reading the stored
            // object instead would keep the Preview on the pre-save render until the
            // row was reselected. Falls back to it because savedScenes is briefly
            // empty while loading, and blanking the Preview then would flicker.
            const saved = rootState.globographics?.savedScenes || []
            const c = saved.find(s => s.id_gh_cena === state.previewCena.id_gh_cena) || state.previewCena
            return {
                id_gh_cena: c.id_gh_cena,
                id_playlist_item: null,
                cena: c,
                dsc_vizrt_template_id: c.dsc_vizrt_template_id || null,
                flg_no_ar: c.flg_no_ar || false,
            }
        }
        if (state.previewSelectionId == null) return null
        for (const pl of state.playlists || []) {
            const found = (pl.items || []).find(i => i.id_playlist_item === state.previewSelectionId)
            if (found) return found
        }
        return null
    },
    // The item currently ON AIR, resolved FRESH from the polled playlists (so a
    // newsroom edit to the on-air graphic flows straight through). The editor
    // follows this whenever it exists: an edit has to land on the template the
    // engine is actually rendering, otherwise a live update goes to the wrong
    // graphic.
    //
    // Only an ACTIVE playlist can have something on air, and the marker alone does not
    // say so: `flg_no_ar` is per-item and outlives its playlist whenever a deactivate
    // does not reach it — a pod dying mid-teardown, or a MOS playlist whose rundown
    // left the feed. The API's own lookup (findOnAirItem) has always required the
    // playlist to be active; this scanned every playlist it had polled, so ONE stale
    // marker anywhere made the Editor open that item with nothing selected and nothing
    // on air, under a banner promising the edit would take precedence on air.
    getterOnAirItem: (state) => {
        for (const pl of state.playlists || []) {
            if (!pl.flg_ativa) continue
            const found = (pl.items || []).find(i => i.flg_no_ar)
            if (found) return found
        }
        return null
    },
    getterOnAirItemPlaylistId: (state) => {
        for (const pl of state.playlists || []) {
            if (!pl.flg_ativa) continue
            if ((pl.items || []).some(i => i.flg_no_ar)) return pl.id_playlist
        }
        return null
    },
    // The playlist that holds the selected item — the inline editor needs it to save
    // MOS-item value edits. Null for a cena (saved via saveVizrtCena, not by playlist).
    getterPreviewItemPlaylistId: (state) => {
        if (state.previewCena || state.previewSelectionId == null) return null
        for (const pl of state.playlists || []) {
            if ((pl.items || []).some(i => i.id_playlist_item === state.previewSelectionId)) return pl.id_playlist
        }
        return null
    },
}

const actions = {
    // --- Playout commands ---
    async outScene({ dispatch, commit }, codProducao) {
        commit('setLastError', null)
        try {
            const { data } = await apiClient.post('playout/out', { codProducao })
            // Nothing is on air now, and the OUT returned 200 — clear the badge from
            // what we already know rather than asking the server to tell us again.
            commit('applyOnAir', {})
            commit('globographics/applyCenaOnAir', null, { root: true })
            dispatch('reconcilePlayoutState', codProducao)
            return data
        } catch (error) {
            const msg = describeError(error, 'Erro ao executar OUT')
            commit('setLastError', msg)
            throw error
        }
    },

    async continueScene({ commit }, codProducao) {
        commit('setLastError', null)
        try {
            const { data } = await apiClient.post('playout/continue', { codProducao })
            return data
        } catch (error) {
            const msg = describeError(error, 'Erro ao executar CONTINUE')
            commit('setLastError', msg)
            throw error
        }
    },

    // --- State ---
    async fetchPlayoutState({ commit }, codProducao) {
        const epoch = onAirEpoch
        try {
            const { data } = await apiClient.get(`playout/state/${codProducao}`)
            if (readIsStale(epoch, codProducao)) return   // describes the world before the last command
            commit('setPlayoutState', data)
            return data
        } catch (error) {
            // The epoch guard applies to the failure path too. Without it a poll that
            // times out AFTER a take wiped the state a confirmed command had just set:
            // the transport dropped to STBY and re-armed Take while the graphic was on
            // air and still highlighted in the list.
            if (readIsStale(epoch, codProducao)) return
            commit('setPlayoutState', null)
        }
    },

    // --- Playlists CRUD ---
    // Silent read — doesn't toggle the shared `loading` flag because this
    // runs on every poll tick and would make the Ativar/Desativar buttons
    // flash. User-initiated actions (activate, deactivate) own that flag.
    //
    // Does NOT clear the previous list before fetching: the 10s polling loop
    // would make the panel disappear/reappear on every tick. Production
    // switches (where stale data WOULD leak) are responsible for calling
    // `clearPlayoutForProductionSwitch` first — see the panel watcher.
    async fetchPlaylists({ commit }, codProducao) {
        commit('setLastError', null)
        const epoch = onAirEpoch
        try {
            const { data } = await apiClient.get(`playout/playlists/${codProducao}`)
            // Dropped rather than merged: setPlaylists replaces the rows wholesale,
            // flg_no_ar included, so a read that crossed a command would undo the marker
            // the command already confirmed. Keeping what we display costs at most one
            // poll cycle of item-list freshness; showing the wrong graphic as on air
            // costs the operator their trust in the panel.
            if (readIsStale(epoch, codProducao)) return
            commit('setPlaylists', data)
            const active = data.find(p => p.flg_ativa)
            commit('setActivePlaylist', active || null)
        } catch (error) {
            const msg = error.response?.data?.error || 'Erro ao carregar playlists'
            commit('setLastError', msg)
        }
    },

    // Called by the panel before switching productions. Wipes the visible
    // state so the next fetch can't flash the prior production's playlists.
    clearPlayoutForProductionSwitch({ commit }) {
        // Invalidate any in-flight read/reconcile: its answer describes the production
        // we just left, and item ids are global so it would look valid here.
        onAirEpoch++
        onAirProducao = null
        commit('setPlaylists', [])
        commit('setActivePlaylist', null)
        commit('setPlayoutState', null)
        commit('setActivationReport', null)
        commit('setPreviewSelection', null)
        commit('setPreviewCue', null)
        commit('setPreviewCueError', null)
    },

    // Select (or toggle off) a playlist item for the big preview. FE-local only.
    // Changing selection drops any prior native cue result (the block re-cues for
    // the new item; a toggle-off clears the preview entirely).
    setPreviewSelection({ commit, dispatch, state }, idItem) {
        const id = idItem == null ? null : Number(idItem)
        const next = state.previewSelectionId === id ? null : id
        if (next !== state.previewSelectionId) {
            commit('setPreviewCue', null)
            commit('setPreviewCueError', null)
        }
        commit('setPreviewCena', null) // item + cena selections are mutually exclusive
        commit('setPreviewSelection', next)
        if (next == null) dispatch('stopPreviewCue')
    },
    // Select (or toggle off) a saved cena for the big preview + inline editor.
    // Clears any playlist-item selection + native cue (cenas show their static render).
    selectPreviewCena({ commit, dispatch, state }, cena) {
        const same = state.previewCena && cena && state.previewCena.id_gh_cena === cena.id_gh_cena
        commit('setPreviewSelection', null)
        commit('setPreviewCue', null)
        commit('setPreviewCueError', null)
        dispatch('stopPreviewCue')
        commit('setPreviewCena', same ? null : (cena || null))
    },
    clearPreviewSelection({ commit, dispatch }) {
        commit('setPreviewSelection', null)
        commit('setPreviewCena', null)
        commit('setPreviewCue', null)
        commit('setPreviewCueError', null)
        dispatch('stopPreviewCue')
    },

    // Cue the selected item on the preview engine for a NATIVE (WebRTC) pre-take
    // preview. Air-safe: the backend refuses (409 PREVIEW_LIVE) while the playlist
    // is on air, so this is only meaningful before the playlist goes live. Resolves
    // the item's playlist from the polled state. Best-effort — on failure the block
    // falls back to the static thumbnail.
    async cuePreviewItem({ commit, state }, { idItem }) {
        const id = Number(idItem)
        let idPlaylist = null
        for (const pl of state.playlists || []) {
            if ((pl.items || []).some(i => i.id_playlist_item === id)) { idPlaylist = pl.id_playlist; break }
        }
        if (!idPlaylist) return null
        commit('setPreviewCueLoading', true)
        commit('setPreviewCueError', null)
        try {
            const { data } = await apiClient.post(`playout/playlists/${idPlaylist}/items/${id}/preview-cue`)
            // Guard against a race: the operator may have changed selection while the
            // cue was in flight — only apply if this item is still the selected one.
            if (state.previewSelectionId === id) {
                commit('setPreviewCue', { idItem: id, signalingUrl: data.signalingUrl, engineKey: data.engineKey })
            }
            return data
        } catch (error) {
            const code = error.response?.data?.code || null
            commit('setPreviewCueError', code || 'CUE_FAILED')
            // Drop any prior cue so the block falls back to the static still instead
            // of keeping a live WebRTC subscription. This matters when the shared
            // engine was reallocated: PREVIEW_ENGINE_BUSY means another production
            // took it, and the old signalingUrl now streams THEIR program — showing
            // it under "pré-visualização" would be a lie. Fall back to the still.
            commit('setPreviewCue', null)
            return null
        } finally {
            commit('setPreviewCueLoading', false)
        }
    },

    // Release the preview and its engine lease (best-effort). Fire-and-forget.
    // Resolve the playlist from the cue's item; but the cue is often already cleared
    // by the selection change that triggered the stop, so fall back to the active
    // playlist — the backend still needs to be told to out the render the preview
    // left on an active playlist's own engine (returning it to idle).
    async stopPreviewCue({ commit, state }) {
        const cue = state.previewCue
        commit('setPreviewCue', null)
        let idPlaylist = null
        if (cue) {
            for (const pl of state.playlists || []) {
                if ((pl.items || []).some(i => i.id_playlist_item === cue.idItem)) { idPlaylist = pl.id_playlist; break }
            }
        }
        if (!idPlaylist && state.activePlaylist) idPlaylist = state.activePlaylist.id_playlist
        if (!idPlaylist) return
        try {
            await apiClient.post(`playout/playlists/${idPlaylist}/preview-cue/stop`)
        } catch (_) {
            // best-effort — a stale lease expires on its own via the TTL
        }
    },

    // Refresh the preview engine lease while actively previewing. The shared engine is
    // reserved only as long as this keeps firing; if the backend reports the lease was
    // lost (another production claimed the engine after ours expired), drop the cue so
    // the block falls back to the static still instead of showing their render.
    async heartbeatPreviewCue({ commit, state }) {
        const cue = state.previewCue
        if (!cue) return
        let idPlaylist = null
        for (const pl of state.playlists || []) {
            if ((pl.items || []).some(i => i.id_playlist_item === cue.idItem)) { idPlaylist = pl.id_playlist; break }
        }
        if (!idPlaylist) return
        try {
            const { data } = await apiClient.post(`playout/playlists/${idPlaylist}/preview-cue/heartbeat`)
            if (data && data.held === false && state.previewCue === cue) {
                commit('setPreviewCue', null)
                commit('setPreviewCueError', 'PREVIEW_ENGINE_HELD')
            }
        } catch (_) {
            // best-effort — a transient failure will retry on the next tick
        }
    },

    // `templates` (Vizrt catalog) and `idGhCenas` (existing cenas) seed the new
    // playlist server-side in one transaction — see POST /playout/playlists.
    async createPlaylist({ dispatch, commit }, { codProducao, nome, templates, idGhCenas }) {
        commit('setLastError', null)
        try {
            const { data } = await apiClient.post('playout/playlists', { codProducao, nome, templates, idGhCenas })
            await dispatch('fetchPlaylists', codProducao)
            return data
        } catch (error) {
            const msg = error.response?.data?.error || 'Erro ao criar playlist'
            commit('setLastError', msg)
            throw error
        }
    },

    async deletePlaylist({ dispatch, commit }, { idPlaylist, codProducao }) {
        commit('setLastError', null)
        // Any report describes an activation of a playlist that is about to stop existing.
        commit('setActivationReport', null)
        try {
            // Deleting the ACTIVE playlist deactivates it server-side, so this moves the
            // marker exactly as a deactivate does — and the transport header reads from
            // playoutState, which nothing here refreshed. Only invalidate when we can also
            // replace what we drop; with no production there is no follow-up read.
            const epoch = codProducao ? invalidateOnAirReads() : null
            await apiClient.delete(`playout/playlists/${idPlaylist}`)
            if (codProducao && !readIsStale(epoch, codProducao)) {
                await Promise.all([
                    dispatch('fetchPlaylists', codProducao),
                    dispatch('fetchPlayoutState', codProducao),
                    dispatch('globographics/loadSavedScenes', { codProducao }, { root: true }),
                ])
            }
        } catch (error) {
            const msg = error.response?.data?.error || 'Erro ao excluir playlist'
            commit('setLastError', msg)
            throw error
        }
    },

    // --- Playlist items ---
    // Attaches cenas the production already has — one round-trip and one
    // playlist refresh instead of a pair per cena. Never creates a cena;
    // importing from the Vizrt catalog is a separate action. Returns
    // { items, skipped, missing, publishErrors } for partial reporting.
    async addItemsToPlaylist({ dispatch, commit }, { idPlaylist, idGhCenas, codProducao }) {
        commit('setLastError', null)
        try {
            const { data } = await apiClient.post(`playout/playlists/${idPlaylist}/items/bulk`, { idGhCenas })
            if (codProducao) await dispatch('fetchPlaylists', codProducao)
            return data
        } catch (error) {
            const msg = error.response?.data?.error || 'Erro ao adicionar itens'
            commit('setLastError', msg)
            throw error
        }
    },

    async removeItemFromPlaylist({ dispatch, commit }, { idPlaylist, idItem, codProducao }) {
        commit('setLastError', null)
        try {
            await apiClient.delete(`playout/playlists/${idPlaylist}/items/${idItem}`)
            if (codProducao) {
                await dispatch('fetchPlaylists', codProducao)
                await dispatch('globographics/loadSavedScenes', { codProducao }, { root: true })
            }
        } catch (error) {
            const msg = error.response?.data?.error || 'Erro ao remover item'
            commit('setLastError', msg)
            throw error
        }
    },

    // Operator content-edit of an inline (MOS) item's field values. Allowed inside
    // a MOS playlist (structural edits stay locked). The edit wins until the next
    // newsroom update for that item (backend reconcile precedence).
    async updatePlaylistItemValues({ dispatch, commit }, { idPlaylist, idItem, valores, titulo, codProducao }) {
        commit('setLastError', null)
        try {
            const { data } = await apiClient.put(`playout/playlists/${idPlaylist}/items/${idItem}/valores`, { valores, titulo })
            if (codProducao) {
                await dispatch('fetchPlaylists', codProducao)
                await dispatch('fetchPlayoutState', codProducao)
            }
            return data
        } catch (error) {
            const msg = error.response?.data?.error || 'Erro ao editar item'
            commit('setLastError', msg)
            throw error
        }
    },

    // Lazy on-view render of an inline item's static preview thumbnail. Used as a
    // fallback for items whose preview wasn't warmed (pre-feature items, or a warm
    // that missed). Best-effort — a Preview Server hiccup returns null, no error.
    async renderPlaylistItemPreview(_ctx, { idItem }) {
        try {
            const { data } = await apiClient.post(`playout/playlists/items/${idItem}/preview`)
            return data
        } catch (error) {
            return null
        }
    },

    async reorderPlaylistItems({ dispatch, commit }, { idPlaylist, items, codProducao }) {
        commit('setLastError', null)
        try {
            await apiClient.put(`playout/playlists/${idPlaylist}/reorder`, { items })
            if (codProducao) await dispatch('fetchPlaylists', codProducao)
        } catch (error) {
            const msg = error.response?.data?.error || 'Erro ao reordenar playlist'
            commit('setLastError', msg)
            throw error
        }
    },

    // --- Playlist groups (subfolders / stories) ---
    async createGrupo({ dispatch, commit }, { idPlaylist, titulo, codProducao }) {
        commit('setLastError', null)
        try {
            const { data } = await apiClient.post(`playout/playlists/${idPlaylist}/grupos`, { titulo })
            if (codProducao) await dispatch('fetchPlaylists', codProducao)
            return data
        } catch (error) {
            const msg = error.response?.data?.error || 'Erro ao criar grupo'
            commit('setLastError', msg)
            throw error
        }
    },

    async updateGrupo({ dispatch, commit }, { idGrupo, titulo, codProducao }) {
        commit('setLastError', null)
        try {
            const { data } = await apiClient.put(`playout/grupos/${idGrupo}`, { titulo })
            if (codProducao) await dispatch('fetchPlaylists', codProducao)
            return data
        } catch (error) {
            const msg = error.response?.data?.error || 'Erro ao renomear grupo'
            commit('setLastError', msg)
            throw error
        }
    },

    async deleteGrupo({ dispatch, commit }, { idGrupo, codProducao }) {
        commit('setLastError', null)
        try {
            await apiClient.delete(`playout/grupos/${idGrupo}`)
            if (codProducao) await dispatch('fetchPlaylists', codProducao)
        } catch (error) {
            const msg = error.response?.data?.error || 'Erro ao excluir grupo'
            commit('setLastError', msg)
            throw error
        }
    },

    async reorderGrupos({ dispatch, commit }, { idPlaylist, order, codProducao }) {
        commit('setLastError', null)
        try {
            await apiClient.put(`playout/playlists/${idPlaylist}/grupos/reorder`, { order })
            if (codProducao) await dispatch('fetchPlaylists', codProducao)
        } catch (error) {
            const msg = error.response?.data?.error || 'Erro ao reordenar grupos'
            commit('setLastError', msg)
            throw error
        }
    },

    async reorderGrupoItems({ dispatch, commit }, { idGrupo, order, codProducao }) {
        commit('setLastError', null)
        try {
            await apiClient.put(`playout/grupos/${idGrupo}/items/reorder`, { order })
            if (codProducao) await dispatch('fetchPlaylists', codProducao)
        } catch (error) {
            const msg = error.response?.data?.error || 'Erro ao reordenar itens do grupo'
            commit('setLastError', msg)
            throw error
        }
    },

    async moveItemToGrupo({ dispatch, commit }, { idPlaylist, idItem, idGrupo, codProducao }) {
        commit('setLastError', null)
        try {
            await apiClient.put(`playout/playlists/${idPlaylist}/items/${idItem}/move`, { idGrupo })
            if (codProducao) await dispatch('fetchPlaylists', codProducao)
        } catch (error) {
            const msg = error.response?.data?.error || 'Erro ao mover item'
            commit('setLastError', msg)
            throw error
        }
    },

    // --- MOS rundowns (read-only discovery) ---
    async fetchMosRundowns({ commit }) {
        commit('setLastError', null)
        try {
            const { data } = await apiClient.get('playout/mos/rundowns')
            return data
        } catch (error) {
            const msg = error.response?.data?.error || 'Erro ao listar rundowns MOS'
            commit('setLastError', msg)
            throw error
        }
    },

    async fetchMosRundown({ commit }, { ncsId, roId }) {
        commit('setLastError', null)
        try {
            const { data } = await apiClient.get('playout/mos/rundown', { params: { ncsId, roId } })
            return data
        } catch (error) {
            const msg = error.response?.data?.error || 'Erro ao ler rundown MOS'
            commit('setLastError', msg)
            throw error
        }
    },

    async importMosRundown({ dispatch, commit }, { ncsId, roId, codProducao }) {
        commit('setLastError', null)
        try {
            const { data } = await apiClient.post('playout/mos/import', { ncsId, roId, codProducao })
            if (codProducao) await dispatch('fetchPlaylists', codProducao)
            return data
        } catch (error) {
            const msg = error.response?.data?.error || 'Erro ao importar rundown MOS'
            commit('setLastError', msg)
            throw error
        }
    },

    // Records the outcome of a take/next/previous on the client from the response
    // the command already returned, then reconciles with the server in the
    // background. Before this, every command was followed by two awaited reads —
    // the playout state (which itself makes an MSE round-trip) and the production's
    // entire saved-cena list — so the control bar could not react until three
    // sequential requests had completed. The command's own response already carries
    // which item went on air, its element and its cena, which is everything the
    // on-air marker needs.
    //
    // `idItem` is taken from the response (`data.idItem`) because next/previous pick
    // the target server-side; the explicit-take caller passes its own as a fallback
    // for an older API that does not return it yet.
    commitOnAirFromCommand({ commit, dispatch }, { idPlaylist, codProducao, data, idItem = null }) {
        const targetId = data?.idItem ?? idItem
        if (targetId == null) {
            // Nothing to mark locally — fall back to a plain reconcile so the UI
            // still catches up without the operator waiting on it. Forget the previous
            // take while we do: the marker has moved somewhere we cannot name, and
            // reapplying the last one we knew would assert a position that is now wrong.
            dispatch('reconcilePlayoutState', codProducao)
            return
        }
        commit('applyOnAir', {
            idPlaylist,
            idItem: targetId,
            elementName: data?.elementName ?? null,
            cena: data?.cena ?? null,
        })
        commit('globographics/applyCenaOnAir', data?.cena?.id ?? null, { root: true })
        dispatch('reconcilePlayoutState', codProducao)
    },

    // Re-reads the authoritative playout state WITHOUT blocking the caller, and
    // drops its answer if another command has landed meanwhile. Deliberately not
    // awaited: it exists to correct the local marker if the server disagrees (a
    // force-activate from another operator, a failed take we misread), not to
    // produce the update the operator is watching for.
    reconcilePlayoutState({ commit }, codProducao) {
        if (!codProducao) return
        const epoch = invalidateOnAirReads()
        onAirProducao = codProducao
        apiClient.get(`playout/state/${codProducao}`)
            .then(({ data }) => {
                if (readIsStale(epoch, codProducao)) return   // a newer command, or another production
                commit('setPlayoutState', data)
                // Move the ROWS too. setPlayoutState only feeds the transport header and
                // the "Cena em Exibição" block; the red pipe and the ON AIR badge come
                // from flg_no_ar on state.playlists. Committing only the former let the
                // two surfaces disagree about what is on television — the header saying
                // one graphic, the highlighted row another — with nothing on screen
                // hinting which was right.
                commit('applyOnAir', {
                    idPlaylist: data?.onAir?.item?.id_playlist ?? null,
                    idItem: data?.onAir?.item?.id_playlist_item ?? null,
                    elementName: data?.onAir?.elementName ?? null,
                    cena: data?.onAir?.id != null ? { id: data.onAir.id, nome: data.onAir.nome } : null,
                })
                commit('globographics/applyCenaOnAir', data?.onAir?.id ?? null, { root: true })
            })
            .catch(() => { /* the 10s panel poll is the backstop */ })
    },

    // --- Playlist activation & navigation ---
    async activatePlaylist({ dispatch, commit }, { idPlaylist, codProducao, force = false }) {
        commit('setLastError', null)
        commit('setActivationReport', null)
        commit('setLoading', true)
        try {
            const url = `playout/playlists/${idPlaylist}/activate${force ? '?force=true' : ''}`
            // Before the request, not after: issuing the command is what makes every
            // earlier read stale, and activation is the slowest command in the panel — an
            // invalidation that waits for it leaves a Take's reconcile free to put the
            // badge back for the whole of that wait.
            const epoch = invalidateOnAirReads()
            const { data } = await apiClient.post(url)
            // ...and skip our own refresh if the operator moved on while we were away:
            // these reads are for THIS production and would otherwise commit into
            // whatever the panel is showing now.
            if (codProducao && !readIsStale(epoch, codProducao)) {
                // The response says which graphics it REFUSED to publish, each with a
                // message written for the operator. Recording it was the missing half of
                // that: the API had built the whole channel and the caller dropped the
                // return value, so an activation that published nothing still looked
                // like a clean success and the reason existed only in a server log.
                // Under the same staleness rule as the reads below — the refusals belong
                // to the production this command was for, not to whatever is on screen
                // now if the operator moved on while we were away.
                commit('setActivationReport', {
                    errors: data?.errors || [],
                    publishing: data?.publishing || null,
                })
                // Three independent reads that were being awaited one after another,
                // adding their full latency to an activation the operator is watching.
                // Activation now publishes only the head of the rundown and fills the
                // rest behind its response, so the item list keeps filling in on the
                // panel's poll — there is nothing here worth serializing for.
                await Promise.all([
                    dispatch('fetchPlaylists', codProducao),
                    dispatch('fetchPlayoutState', codProducao),
                    dispatch('globographics/loadSavedScenes', { codProducao }, { root: true }),
                ])
            }
            return data
        } catch (error) {
            // ENGINE_BUSY is a user-actionable conflict — don't overwrite lastError
            // with a generic message; let the caller surface the modal.
            if (error.response?.data?.code !== 'ENGINE_BUSY') {
                const msg = error.response?.data?.error || 'Erro ao ativar playlist'
                commit('setLastError', msg)
            }
            throw error
        } finally {
            commit('setLoading', false)
        }
    },

    async deactivatePlaylist({ dispatch, commit }, { idPlaylist, codProducao }) {
        commit('setLastError', null)
        // The report describes the activation this command is ending.
        commit('setActivationReport', null)
        try {
            // The server clears flg_no_ar on every cena and item, so the only thing that
            // can put the badge back is an older read resolving late. Invalidated before
            // the request for the same reason as activate.
            const epoch = invalidateOnAirReads()
            const { data } = await apiClient.post(`playout/playlists/${idPlaylist}/deactivate`)
            if (codProducao && !readIsStale(epoch, codProducao)) {
                await Promise.all([
                    dispatch('fetchPlaylists', codProducao),
                    dispatch('fetchPlayoutState', codProducao),
                    dispatch('globographics/loadSavedScenes', { codProducao }, { root: true }),
                ])
            }
            return data
        } catch (error) {
            const msg = error.response?.data?.error || 'Erro ao desativar playlist'
            commit('setLastError', msg)
            throw error
        }
    },

    async playlistNext({ dispatch, commit }, { idPlaylist, codProducao }) {
        commit('setLastError', null)
        try {
            const { data } = await apiClient.post(`playout/playlists/${idPlaylist}/next`)
            dispatch('clearPreviewSelection')
            dispatch('commitOnAirFromCommand', { idPlaylist, codProducao, data })
            return data
        } catch (error) {
            const msg = describeError(error, 'Erro ao avançar na playlist')
            commit('setLastError', msg)
            throw error
        }
    },

    async playlistPrevious({ dispatch, commit }, { idPlaylist, codProducao }) {
        commit('setLastError', null)
        try {
            const { data } = await apiClient.post(`playout/playlists/${idPlaylist}/previous`)
            dispatch('clearPreviewSelection')
            dispatch('commitOnAirFromCommand', { idPlaylist, codProducao, data })
            return data
        } catch (error) {
            const msg = describeError(error, 'Erro ao voltar na playlist')
            commit('setLastError', msg)
            throw error
        }
    },

    async playlistTakeItem({ dispatch, commit }, { idPlaylist, idItem, codProducao }) {
        commit('setLastError', null)
        try {
            const { data } = await apiClient.post(`playout/playlists/${idPlaylist}/take/${idItem}`)
            // What's on air just changed — drop any editing selection so the editor
            // follows the new on-air template instead of a stale row.
            dispatch('clearPreviewSelection')
            dispatch('commitOnAirFromCommand', { idPlaylist, codProducao, data, idItem })
            return data
        } catch (error) {
            const msg = describeError(error, 'Erro ao executar take do item')
            commit('setLastError', msg)
            throw error
        }
    },

}

const mutations = {
    setPlaylists(state, playlists) { state.playlists = playlists },
    setActivePlaylist(state, playlist) { state.activePlaylist = playlist },
    setPlayoutState(state, playoutState) { state.playoutState = playoutState },
    setActivationReport(state, report) { state.activationReport = report },
    // Move the on-air marker locally from what the take/next/previous response
    // already told us. This is not optimistic: the command has returned 200, so the
    // Media Sequencer has taken the element — we are recording a confirmed fact, not
    // predicting one. It exists because the alternative was asking the server what
    // just happened over two more round-trips (one of which hits the MSE again)
    // before the button could react. Pass idItem null to clear (Takeout).
    applyOnAir(state, { idPlaylist = null, idItem = null, elementName = null, cena = null } = {}) {
        for (const pl of state.playlists || []) {
            for (const item of pl.items || []) {
                const onAir = idItem != null && item.id_playlist_item === idItem
                if (item.flg_no_ar !== onAir) item.flg_no_ar = onAir
                // Inline (MOS) items carry a projected cena view whose flag the list
                // also reads; keep the two in step.
                if (item.cena && item.cena.flg_no_ar !== onAir) item.cena.flg_no_ar = onAir
                // The item may have been published on the way through this very Take:
                // activation defers most of a rundown, so the first time an item is
                // taken can also be the first time it exists in the sequencer. Without
                // this the row still reads as unpublished until the next poll.
                if (onAir && elementName) {
                    if (!item.dsc_mse_element_name) item.dsc_mse_element_name = elementName
                    if (item.cena && !item.cena.dsc_mse_element_name) item.cena.dsc_mse_element_name = elementName
                }
            }
        }
        // Only patch an existing state object — fabricating one would invent the
        // playout/engine block the preview depends on. When it is null the
        // reconcile below fills it in.
        if (state.playoutState) {
            state.playoutState = {
                ...state.playoutState,
                onAir: idItem == null ? null : {
                    id: cena?.id ?? null,
                    nome: cena?.nome ?? null,
                    elementName,
                    item: { id_playlist_item: idItem, id_playlist: idPlaylist },
                },
            }
        }
    },
    setLoading(state, loading) { state.loading = loading },
    setLastError(state, error) { state.lastError = error },
    setPreviewSelection(state, idItem) { state.previewSelectionId = idItem },
    setPreviewCena(state, cena) { state.previewCena = cena },
    setPreviewCue(state, cue) { state.previewCue = cue },
    setPreviewCueLoading(state, loading) { state.previewCueLoading = loading },
    setPreviewCueError(state, error) { state.previewCueError = error },
}

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations,
}
